import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { MdDarkMode, MdLightMode, MdSwapHoriz } from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Full-screen Hub shell — no classic sidebar, no classic header chrome.
 * Entirely separate from AdminLayout. Respects Chakra color mode.
 */
export default function HubLayout() {
  const navigate = useNavigate();
  const { logout, staff } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  const displayName = staff?.fullName || staff?.name || staff?.email || 'Staff';

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
  };

  return (
    <Flex direction="column" h="100vh" overflow="hidden" bg="bg.canvas">
      <Flex
        as="header"
        align="center"
        justify="space-between"
        px={{ base: 4, md: 6 }}
        py={3}
        borderBottom="1px solid"
        borderColor="border.subtle"
        bg="bg.surface"
        flexShrink={0}
        gap={3}
      >
        <Flex align="center" gap={3} minW={0}>
          <Heading size="md" color="utkarsh.amber" letterSpacing="-0.02em" mb={0}>
            Utkarsh
          </Heading>
          <Text fontSize="13px" color="text.secondary" display={{ base: 'none', sm: 'block' }}>
            Hub view
          </Text>
        </Flex>
        <Flex align="center" gap={2} flexShrink={0}>
          <Text
            fontSize="13px"
            color="text.primary"
            noOfLines={1}
            display={{ base: 'none', md: 'block' }}
            maxW="160px"
          >
            {displayName}
          </Text>
          <IconButton
            aria-label={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
            onClick={toggleColorMode}
            variant="ghost"
            color="text.secondary"
            size="sm"
            _hover={{ bg: 'bg.input' }}
          />
          <Button
            size="sm"
            leftIcon={<Icon as={MdSwapHoriz} />}
            bg="utkarsh.amber"
            color="utkarsh.ink"
            fontWeight="600"
            _hover={{ bg: 'brand.600' }}
            onClick={() => navigate('/admin/dashboard')}
          >
            Switch to Classic
          </Button>
          <Button
            size="sm"
            variant="ghost"
            color="text.secondary"
            _hover={{ bg: 'bg.input' }}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Flex>
      </Flex>

      <Box as="main" flex="1" minH={0} overflowY="auto" w="100%" bg="bg.canvas">
        <Outlet />
      </Box>
    </Flex>
  );
}
